// `pos` is a content offset (0-based node position) as returned by `doc.forEach`'s offset / Tiptap DragHandle `onNodeChange`.
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';

const LIST_TYPES = ['bulletList', 'orderedList', 'taskList'];

function hasNestedList(node: PMNode): boolean {
  let nested = false;
  node.forEach((child) => {
    if (LIST_TYPES.includes(child.type.name)) nested = true;
  });
  return nested;
}

export function isSectionHead(editor: Editor, pos: number): boolean {
  const { doc } = editor.state;
  if (pos < 0 || pos > doc.content.size) return false;
  const node = doc.nodeAt(pos);
  if (!node) return false;
  if (node.type.name === 'heading') return true;
  if (node.type.name === 'listItem' || node.type.name === 'taskItem') {
    return hasNestedList(node);
  }
  return false;
}

// Returns a HALF-OPEN range [from, to): the block at `to` is excluded.
export function sectionRangeAt(
  editor: Editor,
  pos: number,
): { from: number; to: number } | null {
  const { doc } = editor.state;
  if (pos < 0 || pos > doc.content.size) return null;
  const node = doc.nodeAt(pos);
  if (!node) return null;

  if (node.type.name === 'heading') {
    const level = node.attrs.level as number;
    let to = doc.content.size;
    // Top-level blocks are contiguous: node positions are 0, size0,
    // size0+size1, … so `cursor += sibling.nodeSize` lands exactly on the next
    // sibling's node position and doc.nodeAt(cursor) returns that sibling.
    let cursor = pos + node.nodeSize;
    while (cursor < doc.content.size) {
      const sibling = doc.nodeAt(cursor);
      if (!sibling) break;
      if (sibling.type.name === 'heading' && (sibling.attrs.level as number) <= level) {
        to = cursor;
        break;
      }
      cursor += sibling.nodeSize;
    }
    return { from: pos, to };
  }

  if (
    (node.type.name === 'listItem' || node.type.name === 'taskItem') &&
    hasNestedList(node)
  ) {
    return { from: pos, to: pos + node.nodeSize };
  }

  return null;
}

export function expandRangeToWholeBlocks(
  editor: Editor,
  from: number,
  to: number,
): { from: number; to: number } {
  const { doc } = editor.state;
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const $lo = doc.resolve(Math.min(Math.max(lo, 0), doc.content.size));
  const $hi = doc.resolve(Math.min(Math.max(hi, 0), doc.content.size));
  const start = $lo.before(1);
  const end = $hi.after(1);
  return { from: start, to: end };
}
